import { type MutableRefObject, useRef, useState } from 'react';
import { AVATAR_PEER_RETRY_DELAY, INITIAL_AVATAR_MAX_RETRIES } from '../constants';
import type { AvatarState } from '../types';
import type { UseVoiceLiveTelemetryResult } from './useVoiceLiveTelemetry';

/** Minimal peer-connection controls the fallback state machine drives. */
export interface AvatarPeerControls {
  startAvatar: (iceServers?: RTCIceServer[]) => Promise<void> | void;
  cleanupAvatar: () => void;
  setAvatarState: (state: AvatarState) => void;
}

export interface UseAvatarFallbackOptions {
  /** When false, the user chose audio-only from the start (pre-sets the flag). */
  defaultVideoEnabled: boolean;
  /** Telemetry sink (see useVoiceLiveTelemetry). */
  telemetry: UseVoiceLiveTelemetryResult;
  /** Late-bound peer controls (set after the peer hook is instantiated). */
  peerRef: MutableRefObject<AvatarPeerControls | null>;
  /** Late-bound connection (re)start (set after handleStart is defined). */
  handleStartRef: MutableRefObject<(userId: string, isReconnect?: boolean) => void>;
  wsRef: MutableRefObject<WebSocket | null>;
  iceServersRef: MutableRefObject<RTCIceServer[]>;
  iceServerTimeoutRef: MutableRefObject<number | null>;
  autoStartAvatarRef: MutableRefObject<boolean>;
  savedUserIdRef: MutableRefObject<string>;
  fieldOneSessionIdRef: MutableRefObject<number | null>;
  isVideoEnabledRef: MutableRefObject<boolean>;
  setIsVideoEnabled: (enabled: boolean) => void;
  setErrorMessage: (message: string) => void;
  /** Stop any in-flight audio-only fallback playback. */
  stopFallbackAudioPlayback: () => void;
}

export interface UseAvatarFallbackResult {
  /** True in audio-only fallback (forced or user-chosen). */
  isAudioFallbackRef: MutableRefObject<boolean>;
  /** The WebRTC avatar has connected successfully at least once. */
  avatarEverActiveRef: MutableRefObject<boolean>;
  /** Consecutive initial-avatar establishment failure count. */
  initialAvatarFailuresRef: MutableRefObject<number>;
  /** Dedupe flag: the current attempt has already counted a failure. */
  currentAttemptFailedRef: MutableRefObject<boolean>;
  /** Whether the session degraded to audio-only (drives the banner). */
  isDegraded: boolean;
  /** Funnel for an INITIAL avatar establishment failure (retry → fallback). */
  failAvatarEstablishment: (reason: string) => void;
  /** "Try video again" from the degraded state — a fresh WebRTC attempt. */
  reattemptAvatarFromFallback: () => void;
  /** Reset all fallback gating + the degraded flag (stop / manual retry). */
  reset: () => void;
  /** Cancel a pending peer-retry timeout (connection cleanup). */
  cancelPeerRetry: () => void;
}

/**
 * Owns the audio-only fallback gating state machine: whether the session is in
 * fallback, how many initial avatar attempts have failed, and the retry →
 * fallback escalation. It drives the peer connection (retry / teardown) and the
 * connection layer (reconnect in audio mode) through injected, late-bound refs.
 */
export const useAvatarFallback = ({
  defaultVideoEnabled,
  telemetry,
  peerRef,
  handleStartRef,
  wsRef,
  iceServersRef,
  iceServerTimeoutRef,
  autoStartAvatarRef,
  savedUserIdRef,
  fieldOneSessionIdRef,
  isVideoEnabledRef,
  setIsVideoEnabled,
  setErrorMessage,
  stopFallbackAudioPlayback,
}: UseAvatarFallbackOptions): UseAvatarFallbackResult => {
  // avatarEverActiveRef gates initial-vs-reconnect: once the WebRTC avatar has
  // connected at least once, later drops use the normal WebRTC reconnect path
  // (NOT the audio fallback). initialAvatarFailuresRef counts consecutive
  // failures of the INITIAL avatar establishment and is NOT reset on WS reopen
  // (unlike retryCountRef), so STUN/relay-blocked users deterministically fall
  // back instead of retrying WebRTC forever.
  const avatarEverActiveRef = useRef<boolean>(false);
  const initialAvatarFailuresRef = useRef<number>(0);
  // When defaultVideoEnabled is false the user chose audio from the start.
  // Pre-set the flag so the very first handleStart call uses &mode=audio and
  // skips WebRTC entirely, avoiding the 3 × 45-second SDP-answer timeouts that
  // would otherwise fire before the WebSocket audio path takes over.
  const isAudioFallbackRef = useRef<boolean>(!defaultVideoEnabled);
  // Dedupes the multiple failure signals (connection/ICE/timeout) that a single
  // avatar attempt can emit, so each attempt counts as at most one failure.
  const currentAttemptFailedRef = useRef<boolean>(false);
  const avatarPeerRetryTimeoutRef = useRef<number | null>(null);
  // True when running in audio-only fallback mode after WebRTC avatar setup
  // failed. Drives the "degraded experience" banner and audio-only display.
  const [isDegraded, setIsDegraded] = useState<boolean>(false);

  const cancelPeerRetry = () => {
    if (avatarPeerRetryTimeoutRef.current) {
      window.clearTimeout(avatarPeerRetryTimeoutRef.current);
      avatarPeerRetryTimeoutRef.current = null;
    }
  };

  /**
   * Retry just the WebRTC peer connection on the existing WebSocket session
   * (no full WS reconnect). Tears down the failed peer and re-runs startAvatar
   * after a short delay so transient ICE failures get another chance before we
   * fall back to audio-only.
   */
  const retryAvatarPeer = () => {
    if (avatarPeerRetryTimeoutRef.current) {
      window.clearTimeout(avatarPeerRetryTimeoutRef.current);
    }
    // Tear down the failed peer connection but keep the WebSocket session.
    peerRef.current?.cleanupAvatar();

    avatarPeerRetryTimeoutRef.current = window.setTimeout(() => {
      avatarPeerRetryTimeoutRef.current = null;
      // Guard: WS must still be open and we must not have entered fallback or
      // had the avatar come up via some other path in the meantime.
      if (
        isAudioFallbackRef.current ||
        avatarEverActiveRef.current ||
        !wsRef.current ||
        wsRef.current.readyState !== WebSocket.OPEN
      ) {
        return;
      }
      const servers = iceServersRef.current;
      if (!servers || servers.length === 0) {
        // No ICE servers to retry with — go straight to fallback.
        enterAudioFallback('no-ice-servers-on-retry');
        return;
      }
      console.log('Retrying avatar peer connection...');
      peerRef.current?.startAvatar(servers);
    }, AVATAR_PEER_RETRY_DELAY);
  };

  /**
   * Switch the session to audio-only fallback: tear down WebRTC, show the
   * degraded banner, and reconnect the WebSocket in audio mode so Azure streams
   * the response audio over the socket (played via Web Audio).
   */
  const enterAudioFallback = (reason: string) => {
    if (isAudioFallbackRef.current) {
      return;
    }
    isAudioFallbackRef.current = true;
    setIsDegraded(true);
    setErrorMessage('');
    peerRef.current?.setAvatarState('inactive');
    // Force audio-only display — video can't work on this network.
    isVideoEnabledRef.current = false;
    setIsVideoEnabled(false);

    // Stop any pending peer retry and ICE-server wait.
    if (avatarPeerRetryTimeoutRef.current) {
      window.clearTimeout(avatarPeerRetryTimeoutRef.current);
      avatarPeerRetryTimeoutRef.current = null;
    }
    if (iceServerTimeoutRef.current) {
      window.clearTimeout(iceServerTimeoutRef.current);
      iceServerTimeoutRef.current = null;
    }
    autoStartAvatarRef.current = false;

    console.warn(`Switching to audio-only fallback: ${reason}`);
    telemetry.track('WebRTC:AudioFallbackTriggered', {
      reason,
      failureCount: initialAvatarFailuresRef.current,
      iceServerUrls: telemetry.iceServerUrls(),
      userId: savedUserIdRef.current,
    });

    // Tear down the failed WebRTC peer and reconnect the WS in audio mode.
    // Reuse the existing FieldOne session if we have one (isReconnect=true
    // includes session_id); handleStart appends &mode=audio because
    // isAudioFallbackRef is set.
    peerRef.current?.cleanupAvatar();
    handleStartRef.current(savedUserIdRef.current, !!fieldOneSessionIdRef.current);
  };

  /**
   * Central handler for a failure to establish the INITIAL avatar (WebRTC)
   * connection. Deduped per attempt. Behaviour:
   *  - Already in audio fallback: ignore (the audio path owns the connection).
   *  - Avatar was active before (post-success drop): defer to the normal
   *    WebRTC reconnect path; do nothing special here.
   *  - Otherwise: count the failure and either retry the peer connection or,
   *    once retries are exhausted, switch to the audio-only fallback.
   */
  const failAvatarEstablishment = (reason: string) => {
    if (isAudioFallbackRef.current) {
      return;
    }
    // A successful avatar that later drops is handled by the WebRTC reconnect
    // path (ws.onclose -> attemptReconnect), not the audio fallback.
    if (avatarEverActiveRef.current) {
      return;
    }
    // Dedupe the multiple failure signals (connection/ICE/timeout) a single
    // attempt can emit.
    if (currentAttemptFailedRef.current) {
      return;
    }
    currentAttemptFailedRef.current = true;

    initialAvatarFailuresRef.current += 1;
    const failures = initialAvatarFailuresRef.current;
    console.warn(
      `Initial avatar establishment failed (${failures}/${INITIAL_AVATAR_MAX_RETRIES + 1}): ${reason}`,
    );
    telemetry.track('WebRTC:InitialAvatarFailed', {
      reason,
      failureCount: failures,
      maxAttempts: INITIAL_AVATAR_MAX_RETRIES + 1,
      iceServerUrls: telemetry.iceServerUrls(),
      userId: savedUserIdRef.current,
    });

    if (failures <= INITIAL_AVATAR_MAX_RETRIES) {
      retryAvatarPeer();
    } else {
      enterAudioFallback(reason);
    }
  };

  /**
   * User asked to "Try video again" from the degraded (audio-only) state.
   * Reset the fallback gating and start a fresh session that attempts the
   * WebRTC avatar once more.
   */
  const reattemptAvatarFromFallback = () => {
    stopFallbackAudioPlayback();
    isAudioFallbackRef.current = false;
    avatarEverActiveRef.current = false;
    initialAvatarFailuresRef.current = 0;
    currentAttemptFailedRef.current = false;
    setIsDegraded(false);
    isVideoEnabledRef.current = true;
    setIsVideoEnabled(true);
    setErrorMessage('');
    // Fresh avatar attempt; reuse the FieldOne session if present.
    handleStartRef.current(savedUserIdRef.current, !!fieldOneSessionIdRef.current);
  };

  /** Reset all fallback gating + degraded flag (stop / manual retry). */
  const reset = () => {
    isAudioFallbackRef.current = false;
    avatarEverActiveRef.current = false;
    initialAvatarFailuresRef.current = 0;
    currentAttemptFailedRef.current = false;
    setIsDegraded(false);
  };

  return {
    isAudioFallbackRef,
    avatarEverActiveRef,
    initialAvatarFailuresRef,
    currentAttemptFailedRef,
    isDegraded,
    failAvatarEstablishment,
    reattemptAvatarFromFallback,
    reset,
    cancelPeerRetry,
  };
};
